/**
 * Fail-fast environment validation — mirrors the backend's own
 * `core/configuration/env.schema.ts` philosophy exactly: read every
 * required `import.meta.env` value once, at module load, validate
 * with Zod, and throw immediately with a clear message if anything is
 * missing or malformed, rather than letting `undefined` leak into the
 * app and fail confusingly three layers deep later.
 */
import { z } from 'zod';

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().min(1),
  VITE_APP_NAME: z.string().min(1),
  /** 'recovery' added alongside the existing three — a deployment can be running against `nexao_hmis_recovery` instead of the real production DB (see StatusBar.tsx's own doc comment on why this must stay truthful). 'hmh_training' (desk, 2026-10-09: the status bar on app.csihmh.org said "recovery" although that app runs on the hmh_training database) is the label for the app whose database is hmh_training; it is set at build time by `frontend/.env.production.local` in the hmh release, which Vite reads on top of `.env.production`. */
  VITE_ENVIRONMENT: z.enum(['development', 'staging', 'production', 'recovery', 'hmh_training']),
  /** Deploy-time-only flag — see `routes/guards/MaintenanceGuard.tsx`'s own header for why this isn't a runtime toggle yet. */
  VITE_MAINTENANCE_MODE: z
    .string()
    .optional()
    .transform((value) => value === 'true'),
});

type Env = z.infer<typeof envSchema>;

function loadEnv(): Env {
  const result = envSchema.safeParse(import.meta.env);
  if (!result.success) {
    console.error('Invalid environment configuration:', result.error.flatten().fieldErrors);
    throw new Error('Frontend environment configuration is invalid — see console for details.');
  }
  return result.data;
}

export const env = loadEnv();

export const isProduction = env.VITE_ENVIRONMENT === 'production';
export const isDevelopment = env.VITE_ENVIRONMENT === 'development';
